import React, { useState, useEffect, useMemo, useRef } from "react"

import styled from "styled-components"
import _ from "lodash"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { Button, Spin } from "shared/ui"
import { restoreFileVersionThunk, restoreSharedFileVersionThunk, uploadNewFileVersionFromExistedFileThunk } from "features/file-versions/model/slice/fileVersionsSlice"
import { getMediaViewerType, getContentLink, hasContentLink } from "shared/utils/filesUtils"
import { request } from "shared/api/request"

import { getFilesPreviewOverlayImageDataSelector, getCurrentFilesPreviewItemIndexSelector, getFilesPreviewItemsSelector, getFilesPreviewIsPublicLinkSelector } from "../model/selectors/filesPreviewSelectors"
import { filesPreviewActions } from "../model/slice/filesPreviewSlice"

import { VideoViewer } from "./VideoViewer"
import { AudioViewer } from "./AudioViewer"
import { PdfViewer } from "./PdfViewer"

export const FileConfirmationViewer = React.memo(() => {
  const dispatch = useAppDispatch()
  const overlayImageData = useAppSelector(getFilesPreviewOverlayImageDataSelector)
  const items = useAppSelector(getFilesPreviewItemsSelector)
  const currentIndex = useAppSelector(getCurrentFilesPreviewItemIndexSelector)
  const isPublicLink = useAppSelector(getFilesPreviewIsPublicLinkSelector)
  const previewUrl = overlayImageData.url
  const currentItem = items[currentIndex] as any
  console.log('overlayImageData', overlayImageData)

  const viewerType = useMemo(() => {
    // Для AI изображений - всегда возвращаем 'image'
    if (overlayImageData.type === 'ai') {
      return 'image'
    }
    return getMediaViewerType(overlayImageData.versionData)
  }, [overlayImageData.versionData, overlayImageData.type])

  // Объединенное состояние загрузки
  const [isLoading, setIsLoading] = useState(true)
  const [contentUrl, setContentUrl] = useState('')
  const [coverImageUrl, setCoverImageUrl] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Ref для отслеживания mounted состояния
  const isMountedRef = useRef(true)

  // Cleanup effect для предотвращения утечек памяти
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Валидация URL
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  // Обновление состояния с проверкой mounted
  const updateState = (updates: Partial<{
    contentUrl: string
    coverImageUrl: string
    isLoading: boolean
    error: string | null
  }>) => {
    if (!isMountedRef.current) return

    if (updates.contentUrl !== undefined) setContentUrl(updates.contentUrl)
    if (updates.coverImageUrl !== undefined) setCoverImageUrl(updates.coverImageUrl)
    if (updates.isLoading !== undefined) setIsLoading(updates.isLoading)
    if (updates.error !== undefined) setError(updates.error)
  }

  // Проверка на PDF превью
  const hasPdfPreview = (versionData: any): boolean => {
    return !!versionData?._embedded?.previews?.sizes?.preview && versionData?._embedded?.previews?.sizes?.preview?.type === 'pdf'
  }

  // Получение preview ссылки
  const getPreviewLink = (versionData: any): string => {
    if (!versionData?._embedded?.previews?.sizes?.preview) {
      return ''
    }

    const urlTemplate = _.template(versionData._embedded.previews.url_template, { interpolate: /\{(.+?)\}/g })
    const url = urlTemplate({ size_name: 'preview' }) + `&jwt=${versionData._embedded.previews.jwt}`

    return url
  }

  // Загрузка content URL для медиа файлов
  const loadMediaContent = async (versionData: any) => {
    const contentLink = getContentLink(versionData)

    if (!isValidUrl(contentLink)) {
      throw new Error('Invalid content URL')
    }

    const contentData = await request('GET', contentLink, {}, { host: null }) as { _links: { data: { href: string } } }
    const finalContentUrl = contentData._links.data.href

    if (!isValidUrl(finalContentUrl)) {
      throw new Error('Invalid final content URL')
    }

    return finalContentUrl
  }

  useEffect(() => {
    setIsLoading(true)
    setError(null)
    setContentUrl('')
    setCoverImageUrl('')

    const loadContentUrl = async () => {
      // Early return для типов, не требующих content URL
      if (viewerType !== 'video' && viewerType !== 'audio' && viewerType !== 'pdf') {
        updateState({ contentUrl: '', isLoading: false })
        return
      }

      const versionData = overlayImageData.versionData

      // Early return если нет content link
      if (!hasContentLink(versionData)) {
        updateState({ contentUrl: '', isLoading: false })
        return
      }

      try {
        let finalContentUrl = ''

        // Для PDF файлов с превью используем preview link
        if (viewerType === 'pdf' && hasPdfPreview(versionData)) {
          finalContentUrl = getPreviewLink(versionData)
        } else {
          // Для остальных медиа файлов используем content link
          finalContentUrl = await loadMediaContent(versionData)
        }

        if (!isMountedRef.current) return

        updateState({ contentUrl: finalContentUrl })

        // Для аудио устанавливаем cover image только если есть preview
        if (viewerType === 'audio' && previewUrl && isValidUrl(previewUrl) && previewUrl.trim() !== '') {
          // Дополнительная проверка - пытаемся загрузить изображение
          const img = new Image()
          img.onload = () => {
            if (isMountedRef.current) {
              updateState({ coverImageUrl: previewUrl })
            }
          }
          img.onerror = () => {
            // Если изображение не загружается, оставляем coverImageUrl пустым
            console.warn('Cover image failed to load:', previewUrl)
          }
          img.src = previewUrl
        }
      } catch (error) {
        console.error('Failed to load content URL:', error)
        updateState({
          error: error instanceof Error ? error.message : 'Failed to load content',
          contentUrl: ''
        })
      } finally {
        updateState({ isLoading: false })
      }
    }

    loadContentUrl()
  }, [previewUrl, viewerType, overlayImageData.versionData])

  const handleCancel = () => {
    dispatch(filesPreviewActions.clearOverlayImageData())
  }

  const handleSave = () => {
    if (overlayImageData.type === 'version' && overlayImageData.versionId) {

      if (isPublicLink) {
        dispatch(restoreSharedFileVersionThunk({
          currentItemId: currentItem.id,
          versionId: overlayImageData.versionId,
          callback: () => {
            dispatch(filesPreviewActions.clearOverlayImageData())
          }
        }))
      } else {
        dispatch(restoreFileVersionThunk({
          currentItemId: currentItem.id,
          versionId: overlayImageData.versionId,
          callback: () => {
            dispatch(filesPreviewActions.clearOverlayImageData())
          }
        }))
      }
    }

    if (overlayImageData.type === 'ai' && overlayImageData.versionId) {
      dispatch(uploadNewFileVersionFromExistedFileThunk({
        nodeId: currentItem.id,
        uploadedFileId: overlayImageData.versionId,
        callback: () => {
          dispatch(filesPreviewActions.clearOverlayImageData())
        }
      }))
    }
  }

  const handleImageLoad = () => {
    if (isMountedRef.current) {
      setIsLoading(false)
    }
  }

  const handleImageError = () => {
    if (isMountedRef.current) {
      setIsLoading(false)
      setError('Failed to load image')
    }
  }

  const handleMediaLoad = () => {
    if (isMountedRef.current) {
      setIsLoading(false)
      setError(null)
    }
  }

  const handleMediaError = () => {
    if (isMountedRef.current) {
      setIsLoading(false)
      setError('Failed to load media')
    }
  }

  const getMessage = () => {
    if (overlayImageData.type === 'version') {
      return 'This is a previous version. Saving it will create a new version based on this file.'
    }

    return 'This version was generated by AI assistant. Saving it will create a new version based on this file.'
  }

  return (
    <SContentWrapper>
      <SHead>
        <SHeadMessage>
          {getMessage()}
        </SHeadMessage>

        <SHeadButtons>
          <Button type="default"
            size="middle"
            onClick={handleCancel}
          >
            Cancel
          </Button>

          <SSaveButton
            type="primary"
            size="middle"
            onClick={handleSave}
          >
            Save
          </SSaveButton>
        </SHeadButtons>
      </SHead>

      <SMediaWrapper>
        {error && (
          <SErrorContainer>
            <p>
Error:
              {error}
            </p>
          </SErrorContainer>
        )}

        {!error && viewerType === 'video' && (
          <SVideoContainer>
            {(isLoading || !contentUrl) && (
              <SSpinnerWrapper>
                <Spin size="large" />
              </SSpinnerWrapper>
            )}

            {contentUrl && (
              <VideoViewer
                src={contentUrl + '#t=0.001'}
                onCanPlay={handleMediaLoad}
                onError={handleMediaError}
                annotations={[]}
                comments={[]}
                isCommentsLoading={false}
                isAssetPlaying={false}
                userName=""
                selectedAnnotation={null}
                newAnnotation={null}
                isShowAnnotationLayer={false}
                isShowAnnotationMarkers={false}
                isCreatingNewAnnotation={false}
                creatingNewAnnotationMode={null}
                onAnnotationSubmit={() => undefined}
                onAnnotationDelete={() => undefined}
                onAnnotationEdit={() => undefined}
                onAnnotationResolve={() => undefined}
              />
            )}
          </SVideoContainer>
        )}

        {!error && viewerType === 'audio' && (
          <SAudioContainer>
            {(isLoading || !contentUrl) && (
              <SSpinnerWrapper>
                <Spin size="large" />
              </SSpinnerWrapper>
            )}

            {contentUrl && (
              <AudioViewer
                src={contentUrl + '#t=0.001'}
                onCanPlay={handleMediaLoad}
                onError={handleMediaError}
                coverImageUrl={coverImageUrl}
              />
            )}
          </SAudioContainer>
        )}

        {!error && viewerType === 'pdf' && (
          <SPdfContainer>
            {isLoading && (
              <SSpinnerWrapper>
                <Spin size="large" />
              </SSpinnerWrapper>
            )}

            {contentUrl ? (
              <PdfViewer
                onLoad={handleMediaLoad}
                src={`/pdf-viewer/web/viewer.html?file=${encodeURIComponent(contentUrl)}`}
              />
            ) : !isLoading && (
              <SFallbackContainer>
                <p>
PDF preview not available
                </p>
              </SFallbackContainer>
            )}
          </SPdfContainer>
        )}

        {!error && viewerType === 'image' && (
          <SImgContainer>
            {isLoading && (
              <SSpinnerWrapper>
                <Spin size="large" />
              </SSpinnerWrapper>
            )}

            <SImg
              src={previewUrl}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ display: isLoading ? 'none' : 'block' }}
            />
          </SImgContainer>
        )}

        {!error && (viewerType === 'unknown' || (viewerType === 'video' && !contentUrl && !isLoading) || (viewerType === 'audio' && !contentUrl && !isLoading)) && (
          <SFallbackContainer>
            <p>
Preview not available for this file type
            </p>
          </SFallbackContainer>
        )}
      </SMediaWrapper>
    </SContentWrapper>
  )
})

const SContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const SMediaWrapper = styled.div`
  width: 100%;
  height: calc(100% - 56px);
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background-color: var(--color-black);
`

const SImgContainer = styled.div`
  position: relative;
  height: calc(100% - 100px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px;
  width: 100%;
`

const SVideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const SAudioContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const SPdfContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const SFallbackContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: rgba(255, 255, 255, 0.45);
  font-size: 16px;
`

const SErrorContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ff4d4f;
  font-size: 16px;
  background-color: rgba(255, 77, 79, 0.1);
  border: 1px solid rgba(255, 77, 79, 0.3);
  border-radius: 4px;
  margin: 20px;
  padding: 20px;
  text-align: center;
`

const SImg = styled.img`
  height: 100%;
  width: 100%;
  display: block;
  margin: 0;
  user-select: none;
  object-fit: contain;
  transition: transform 0.3s ease-out;

  &.not-loaded {
    display: none;
  }
`

const SHead = styled.div`
  background-color: #1F1F1F;
  min-height: 56px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #303030;
  border-right: 1px solid #303030;
`

const SHeadMessage = styled.div`
  max-width: 613px;
  font-size: 14px;
  font-weight: 400;
  color: #FFFFFFA6;
`

const SHeadButtons = styled.div`
  margin-left: auto;
  display: flex;
  gap: 12px;
`

const SSpinnerWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
`

const SSaveButton = styled(Button)`
  box-shadow: none !important;
  
  &:hover {
    box-shadow: none !important;
  }
  
  &:focus {
    box-shadow: none !important;
  }
  
  &:active {
    box-shadow: none !important;
  }
`
